import { DrEntity } from './dr-entity';
import { View, ViewType } from './view';
import * as _ from 'underscore';
import { DrObject, DrObjectType } from '../models/dr-object';

export class ComparisonViewModel extends DrObject {
    model: Partial<Comparison>;

    constructor(value?: Partial<Comparison>) {
        super(DrObjectType.COMPARISONS);
        if (value) {
            this.model = value;
        }
    }
}

export class Comparison extends DrEntity {
    name: string;
    description: string;
    outOfDate: boolean;
    archived: boolean;
    viewA: View;
    viewAcomplimentB: View;
    viewAdisjunctionB: View;
    viewAintersectionB: View;
    viewB: View;
    viewBcomplimentA: View;

    // off-model display values
    iconClass: string;
    iconColor: string;
    docListHandle: string;
    iconClassDocumentSetA: string;
    iconColorDocumentSetA: string;
    iconClassDocumentSetB: string;
    iconColorDocumentSetB: string;

    constructor(value?: Partial<Comparison>) {
        super(value);
        if (value) {
            Object.assign(this, value);

            // turning them manually into view models is needed since obj.assign doesn't do that
            if (value.viewA) {
                this.viewA = new View(value.viewA);
            }
            if (value.viewAcomplimentB) {
                this.viewAcomplimentB = new View(value.viewAcomplimentB);
            }
            if (value.viewAdisjunctionB) {
                this.viewAdisjunctionB = new View(value.viewAdisjunctionB);
            }
            if (value.viewAintersectionB) {
                this.viewAintersectionB = new View(value.viewAintersectionB);
            }
            if (value.viewB) {
                this.viewB = new View(value.viewB);
            }
            if (value.viewBcomplimentA) {
                this.viewBcomplimentA = new View(value.viewBcomplimentA);
            }
            this.iconClass = 'fa-exchange';
            this.iconColor = 'color-purple-02';

            if (this.viewA) {
                const displayAttr = _.find(this.viewA.attributes, entry => entry.name === 'displayValue');
                switch (this.viewA.viewType) {
                    case ViewType.UNASSIGNED_CUSTODIAN: {
                        this.iconClassDocumentSetA = 'fa-user-circle';
                        this.iconColorDocumentSetA = 'color-grey-08';
                        break;
                    }
                    case ViewType.CUSTODIAN: {
                        this.iconClassDocumentSetA = 'fa-user-circle';
                        this.iconColorDocumentSetA = 'color-blue-01';
                        break;
                    }
                    case ViewType.UNASSIGNED_BATCH:
                    case ViewType.BATCH: {
                        this.iconClassDocumentSetA = 'fa-archive';
                        this.iconColorDocumentSetA = this.viewA.viewType === ViewType.UNASSIGNED_BATCH ? 'color-grey-08' : 'color-blue-01';
                        break;
                    }
                    case ViewType.UNASSIGNED_MEDIAID:
                    case ViewType.MEDIAID: {
                        this.iconClassDocumentSetA = 'fa-laptop';
                        this.iconColorDocumentSetA = this.viewA.viewType === ViewType.UNASSIGNED_MEDIAID ? 'color-grey-08' : 'color-blue-01';
                        break;
                    }
                    case ViewType.GROUP: { // This is Dynamic folder
                        this.iconClassDocumentSetA = 'fa-folder';
                        this.iconColorDocumentSetA = displayAttr ? displayAttr.value : 'color-tan-07'; // default color
                        break;
                    }
                    case ViewType.TAG: {
                        this.iconClassDocumentSetA = 'fa-tag';
                        this.iconColorDocumentSetA = displayAttr ? displayAttr.value : 'color-tan-07'; // default color
                        break;
                    }
                    case ViewType.CORPUS_RESULT:
                    case ViewType.CORPUS_SET_RESULT:
                    case ViewType.WORKING_SET_RESULT: { // Search History
                        this.iconClassDocumentSetA = 'fa-search';
                        this.iconColorDocumentSetA = 'color-tan-07';
                        this.viewA.name = this.formatSearchViewName(this.viewA);
                        break;
                    }
                }
            }
            if (this.viewB) {
                const displayAttr = _.find(this.viewB.attributes, entry => entry.name === 'displayValue');
                switch (this.viewB.viewType) {
                    case ViewType.UNASSIGNED_CUSTODIAN: {
                        this.iconClassDocumentSetB = 'fa-user-circle';
                        this.iconColorDocumentSetB = 'color-grey-08';
                        break;
                    }
                    case ViewType.CUSTODIAN: {
                        this.iconClassDocumentSetB = 'fa-user-circle';
                        this.iconColorDocumentSetB = 'color-blue-01';
                        break;
                    }
                    case ViewType.UNASSIGNED_BATCH:
                    case ViewType.BATCH: {
                        this.iconClassDocumentSetB = 'fa-archive';
                        this.iconColorDocumentSetB = this.viewB.viewType === ViewType.UNASSIGNED_BATCH ? 'color-grey-08' : 'color-blue-01';
                        break;
                    }
                    case ViewType.UNASSIGNED_MEDIAID:
                    case ViewType.MEDIAID: {
                        this.iconClassDocumentSetB = 'fa-laptop';
                        this.iconColorDocumentSetB = this.viewB.viewType === ViewType.UNASSIGNED_MEDIAID ? 'color-grey-08' : 'color-blue-01';
                        break;
                    }
                    case ViewType.GROUP: { // This is Dynamic folder
                        this.iconClassDocumentSetB = 'fa-folder';
                        this.iconColorDocumentSetB = displayAttr ? displayAttr.value : 'color-tan-07'; // default color
                        break;
                    }
                    case ViewType.TAG: {
                        this.iconClassDocumentSetB = 'fa-tag';
                        this.iconColorDocumentSetB = displayAttr ? displayAttr.value : 'color-tan-07'; // default color
                        break;
                    }
                    case ViewType.CORPUS_RESULT:
                    case ViewType.CORPUS_SET_RESULT:
                    case ViewType.WORKING_SET_RESULT: { // Search History
                        this.iconClassDocumentSetB = 'fa-search';
                        this.iconColorDocumentSetB = 'color-tan-07';
                        this.viewB.name = this.formatSearchViewName(this.viewB);
                        break;
                    }
                }
            }
        }
        this.iconClass = 'fa-exchange';
        this.iconColor = 'color-purple-02';
        this.docListHandle = this.viewAintersectionB ? this.viewAintersectionB.handle : null;
    }

    get currentComparisonView(): View {
        if (this.docListHandle) {
            if (this.viewAcomplimentB && this.viewAcomplimentB.handle === this.docListHandle) {
                return this.viewAcomplimentB;
            }
            if (this.viewAdisjunctionB && this.viewAdisjunctionB.handle === this.docListHandle) {
                return this.viewAdisjunctionB;
            }
            if (this.viewAintersectionB && this.viewAintersectionB.handle === this.docListHandle) {
                return this.viewAintersectionB;
            }
            if (this.viewBcomplimentA && this.viewBcomplimentA.handle === this.docListHandle) {
                return this.viewBcomplimentA;
            }
        }
        return new View({ name: this.name, handle: this.handle });
    }

    formatSearchViewName(view: View): string {
        if (view && view.name) {
            if (view.name.startsWith('Searching for ')) {
                return view.name.replace('Searching for ', '');
            } else if (view.name.trim().startsWith('Searching ')) {
                return view.name.trim().replace('Searching ', '');
            }
            return view.name;
        }
        return '';
    }
}
